<template>
  <div class="toolbar">
    <div class="toolbar__row">
      <!-- Pencarian dengan penundaan; tombol bersih muncul saat ada isi -->
      <div class="toolbar__search">
        <i class="bi bi-search toolbar__search-icon" aria-hidden="true"></i>
        <input
          ref="input"
          v-model="teks"
          type="search"
          class="ui-input toolbar__input"
          :placeholder="placeholder"
          :aria-label="placeholder"
          @keydown.esc="bersihkan"
        />
        <span v-if="mencari" class="ui-spinner toolbar__spinner" aria-hidden="true"></span>
        <button
          v-else-if="teks"
          type="button"
          class="toolbar__clear"
          aria-label="Bersihkan pencarian"
          @click="bersihkan"
        >
          <i class="bi bi-x-lg" aria-hidden="true"></i>
        </button>
      </div>

      <div class="toolbar__actions">
        <slot name="filters" />
        <slot name="actions" />
      </div>
    </div>

    <!-- Ringkasan saringan aktif, masing-masing bisa dilepas satu per satu -->
    <transition name="chips">
      <div v-if="chipAktif.length" class="toolbar__chips">
        <span class="toolbar__chips-label">Saringan:</span>
        <button
          v-for="c in chipAktif"
          :key="c.key"
          type="button"
          class="toolbar__chip"
          @click="$emit('clear-filter', c.key)"
        >
          <span>{{ c.label }}</span>
          <i class="bi bi-x" aria-hidden="true"></i>
        </button>
        <button type="button" class="toolbar__chip-reset" @click="$emit('clear-all')">
          Bersihkan semua
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * Bilah pencarian dan saringan untuk tabel admin.
 *
 * Dua masalah yang diselesaikan:
 *
 * 1. PENUNDAAN KETIKAN. Halaman lama memanggil API pada setiap penekanan
 *    tombol. Mengetik "budi" berarti empat permintaan, dan karena respons
 *    bisa datang tidak berurutan, hasil pencarian "bud" kadang menimpa
 *    hasil "budi". Di sini permintaan baru dikirim setelah pengguna
 *    berhenti mengetik selama 350 ms.
 *
 * 2. SARINGAN TIDAK TERLIHAT. Ketika daftar tampak kosong, pengguna sering
 *    tidak sadar masih ada saringan aktif dari sesi sebelumnya. Sekarang
 *    setiap saringan tampil sebagai chip yang bisa dilepas.
 */
export default {
  name: 'DataToolbar',

  props: {
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: 'Cari...' },
    mencari: { type: Boolean, default: false },
    /** Contoh: [{ key: 'status', label: 'Status: Lunas' }] */
    filters: { type: Array, default: () => [] },
    delay: { type: Number, default: 350 },
  },

  emits: ['update:modelValue', 'search', 'clear-filter', 'clear-all'],

  data() {
    return { teks: this.modelValue, timer: null };
  },

  computed: {
    chipAktif() {
      return this.filters.filter((f) => f && f.label);
    },
  },

  watch: {
    modelValue(v) {
      if (v !== this.teks) this.teks = v;
    },

    teks(v) {
      this.$emit('update:modelValue', v);
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.$emit('search', v.trim()), this.delay);
    },
  },

  mounted() {
    // Tekan "/" di mana saja untuk melompat ke kotak pencarian.
    document.addEventListener('keydown', this.pintasan);
  },

  beforeUnmount() {
    clearTimeout(this.timer);
    document.removeEventListener('keydown', this.pintasan);
  },

  methods: {
    pintasan(e) {
      const diDalamInput = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName);
      if (e.key === '/' && !diDalamInput) {
        e.preventDefault();
        this.$refs.input?.focus();
      }
    },

    bersihkan() {
      this.teks = '';
      clearTimeout(this.timer);
      this.$emit('search', '');
      this.$refs.input?.focus();
    },
  },
};
</script>

<style scoped>
.toolbar { display: flex; flex-direction: column; gap: var(--space-3); margin-bottom: var(--space-5); }

.toolbar__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.toolbar__search {
  position: relative;
  flex: 1 1 260px;
  min-width: 220px;
}

.toolbar__search-icon {
  position: absolute;
  left: var(--space-4);
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  font-size: 0.9rem;
  pointer-events: none;
}

.toolbar__input { padding-left: 2.5rem; padding-right: 2.5rem; }

/* Hilangkan tombol silang bawaan peramban agar tidak dobel dengan milik kita */
.toolbar__input::-webkit-search-cancel-button { display: none; }

.toolbar__clear,
.toolbar__spinner {
  position: absolute;
  right: var(--space-3);
  top: 50%;
  transform: translateY(-50%);
}

.toolbar__clear {
  display: grid;
  place-items: center;
  width: 32px; height: 32px;
  min-width: 32px; min-height: 32px;
  font-size: 0.8rem;
  color: var(--text-muted);
  background: var(--bg-sunken);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.toolbar__clear:hover { background: var(--danger-500); color: #fff; }

.toolbar__spinner { border-color: var(--border-strong); border-top-color: var(--accent); }

.toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
}

.toolbar__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.toolbar__chips-label {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-muted);
}

.toolbar__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0.22rem var(--space-3);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-brand);
  background: var(--accent-subtle);
  border: 1px solid color-mix(in srgb, var(--accent) 25%, transparent);
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: background-color var(--transition-fast);
}

.toolbar__chip:hover { background: color-mix(in srgb, var(--accent) 20%, transparent); }

.toolbar__chip-reset {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-muted);
  background: none;
  border: none;
  text-decoration: underline;
  cursor: pointer;
}

.toolbar__chip-reset:hover { color: var(--danger-500); }

.chips-enter-active, .chips-leave-active { transition: opacity var(--transition-fast), transform var(--transition-fast); }
.chips-enter-from, .chips-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
